// @ts-strict-ignore
import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Trans } from "react-i18next/icu.macro";

import { BodyText, Button, FieldLabel, FlexBox, Input, StatusText } from "@braze/beacon-core";
import type { ButtonLoadingState } from "@braze/beacon-hooks";

import useI18nNamespace from "@services/useI18nNamespace";

import { ErrorCode, getErrorMessage, useValidateSms, useVerifySms } from "../../../../hooks";
import { PhoneNumberInputField } from "../../PhoneNumberInputField";
import { getPhoneCodeFromCountryStringCode, isAllDigits } from "../../utils";
import type { VerifyContactDetailsType } from "../types";
import { CodeVerificationButton } from "./SmsVerification.style";
import type { SmsVerificationProps } from "./types";

export const SmsVerification = ({
  onVerificationComplete,
  setShowSalesBanner,
}: SmsVerificationProps): React.ReactElement => {
  const { t, k } = useI18nNamespace("free-trial-registration");

  const { watch, getFieldState, control, setError, trigger } = useFormContext<VerifyContactDetailsType>();
  const phoneNumber = watch("phoneNumber");
  const phoneNumberCountry = watch("phoneNumberCountry");
  const [maxSendAttemptsErrorMessage, setMaxSendAttemptsErrorMessage] = useState<string>();
  const [reachedMaxSendAttempts, setReachedMaxSendAttempts] = useState<boolean>(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>();
  const [shouldValidateVerificationCode, setShouldValidateVerificationCode] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isDisabled, setIsDisabled] = useState(false);
  const SALES_BANNER_DELAY = 2000;
  const RESEND_BUTTON_DELAY = 30000;
  const clearErrorMessage = useCallback(() => setErrorMessage(""), [setErrorMessage]);
  const { mutateAsync: verifySms, isLoading: isVerifySmsLoading } = useVerifySms({
    onError: (errorCode) => {
      const errorMessage = getErrorMessage(t, errorCode);
      setError("phoneNumber", { message: errorMessage });
      if (errorCode === ErrorCode.ReachedMaxSendAttempts) {
        setMaxSendAttemptsErrorMessage(errorMessage);
        setReachedMaxSendAttempts(true);
      }
    },
    onSuccess: () => {
      setVerificationCodeSent(true);
    },
  });

  useEffect(() => {
    const timeout = setTimeout(
      () => setShowSalesBanner(reachedMaxSendAttempts),
      reachedMaxSendAttempts ? SALES_BANNER_DELAY : 0
    );
    return () => clearTimeout(timeout);
  }, [setShowSalesBanner, reachedMaxSendAttempts]);

  const { mutateAsync: validateSms, isLoading: isValidateSms } = useValidateSms({
    onError: setErrorMessage,
    onSuccess: () => {
      setReachedMaxSendAttempts(false);
      onVerificationComplete();
    },
  });

  const getButtonLoadingState = (isLoading: boolean): ButtonLoadingState => (isLoading ? "loading" : "idle");

  const verifySmsButtonRef = useRef();
  const validateSmsButtonRef = useRef();
  const resendButtonRef = useRef();

  const invalidVerificationCode = useMemo(() => {
    if (shouldValidateVerificationCode && (!isAllDigits(verificationCode) || verificationCode.length !== 6)) {
      return t("sms-verification-code-status-text", "Please enter a valid 6-digit verification code");
    }
    return null;
  }, [t, shouldValidateVerificationCode, verificationCode]);

  const phoneNumberCountryCode = getPhoneCodeFromCountryStringCode(phoneNumberCountry);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const sendVerificationCode = useCallback(async () => {
    setIsDisabled(true);
    await trigger(["phoneNumber", "phoneNumberCountry"]);
    // Check if phone number is invalid
    if (!phoneNumber || getFieldState("phoneNumber").invalid || getFieldState("phoneNumberCountry").invalid) {
      return;
    }
    // API call to send verification code
    await verifySms({
      country_code: phoneNumberCountryCode,
      phone_number: phoneNumber,
    });

    // Set a 30-second timer to re-enable the button
    await delay(RESEND_BUTTON_DELAY);
    setIsDisabled(false);
  }, [phoneNumber, phoneNumberCountryCode, verifySms, getFieldState, trigger]);

  const verifyVerificationCode = useCallback(async () => {
    // API call to verify verification code
    await validateSms({
      verification_code: verificationCode.toString(),
    });
  }, [verificationCode, validateSms]);

  const handleSmsVerificationCodeBlur = useCallback(() => {
    setShouldValidateVerificationCode(true);
  }, [setShouldValidateVerificationCode]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        // check if the key is not being held down
        if (!event.repeat) {
          if (verificationCodeSent) {
            verifyVerificationCode();
          } else {
            sendVerificationCode();
          }
        }
      }
    },
    [verificationCodeSent, verifyVerificationCode, sendVerificationCode]
  );

  if (verificationCodeSent) {
    return (
      <>
        <BodyText id="smsVerificationCodeSentInfo">
          <Trans i18nKey={k("resend-verification-code-description")}>
            A verification code has been sent to{" "}
            <strong>
              +{phoneNumberCountryCode} {phoneNumber}
            </strong>
            . If you have not received a code, please wait 30 seconds before requesting a new one.
          </Trans>
        </BodyText>
        <FlexBox mt="sm" alignItems="flex-start">
          <Button
            ref={resendButtonRef}
            id="resendVerificationSmsButton"
            name="resendVerificationSmsButton"
            variant="quaternary"
            intent="info"
            onClick={sendVerificationCode}
            loadingState={getButtonLoadingState(isVerifySmsLoading)}
            disabled={reachedMaxSendAttempts || isDisabled}
          >
            {t("resend-verification-code-button-text", "Resend verification code")}
          </Button>
        </FlexBox>
        {reachedMaxSendAttempts && <StatusText status="danger">{maxSendAttemptsErrorMessage}</StatusText>}
        <FieldLabel
          data-cy="smsVerificationCodeField"
          label={t("phone-number-code-label", "Code")}
          htmlFor="phoneNumberVerificationCode"
          status="danger"
          statusText={invalidVerificationCode || errorMessage}
        >
          <Input
            type="text"
            pattern="[0-9]*"
            id="phoneNumberVerificationCode"
            placeholder={t("phone-six-digit-code-placeholder", "Enter 6-digit code")}
            fullWidth
            value={verificationCode}
            onBlur={handleSmsVerificationCodeBlur}
            invalid={!!invalidVerificationCode || !!errorMessage}
            onChange={(e) => setVerificationCode(e.target.value)}
            onFocusCapture={clearErrorMessage}
            onKeyDown={handleKeyDown}
          />
        </FieldLabel>
        <Button
          ref={validateSmsButtonRef}
          name="verifySmsVerificationCodeButton"
          id="verifySmsVerificationCodeButton"
          size="full"
          disabled={!!invalidVerificationCode || !verificationCode}
          onClick={verifyVerificationCode}
          loadingState={getButtonLoadingState(isValidateSms)}
        >
          {t("verify-phone-number", "Verify")}
        </Button>
      </>
    );
  }

  return (
    <FlexBox direction="column">
      <PhoneNumberInputField
        label={t("phone-number-label", "Mobile phone number")}
        phoneNumberFieldName="phoneNumber"
        phoneNumberCountryFieldName="phoneNumberCountry"
        control={control}
        onKeyDown={handleKeyDown}
        disabled
      />
      <CodeVerificationButton
        ref={verifySmsButtonRef}
        id="verifySmsButton"
        name="verifySmsButton"
        size="full"
        onClick={sendVerificationCode}
        loadingState={getButtonLoadingState(isVerifySmsLoading)}
      >
        {t("send-verification-code", "Send code")}
      </CodeVerificationButton>
    </FlexBox>
  );
};
